import { useCallback } from 'react'

import { isFunction } from 'lodash'
import { onError, onSuccess } from '../../apiHelper'
import { isObject } from '../..'
import { createSchoolPeriod, deleteSchoolPeriod, updateSchoolPeriod } from '../schoolPeriods'
import { SchoolPeriod } from '../../entities/schoolPeriod'
import { UpdateModes } from '../../enums/enums'
import { PeriodTypes } from '../../timePeriods'
import { initEuropeanDate } from '../../momentjs'
import { Actions } from '../../events'

const useSchoolPeriod = ({ user, t, callback, mode = UpdateModes.OPTIMISTIC }) => {
  const upsert = useCallback((schoolPeriod, previousState) => {
    const config = schoolPeriod.id === -1 ? { request: createSchoolPeriod, action: Actions.CREATE } : { request: updateSchoolPeriod, action: Actions.UPDATE }

    if (mode === UpdateModes.OPTIMISTIC && isFunction(callback)) {
      callback(schoolPeriod, config.action)
    }

    return config.request(user, schoolPeriod).then(response => {
      if (isFunction(callback) && isObject(response.data)) {
        const newData = new SchoolPeriod(response.data)

        if (config.action === Actions.CREATE) {
          if (mode === UpdateModes.OPTIMISTIC) {
            callback(schoolPeriod, Actions.DELETE, newData, Actions.CREATE)
          } else {
            callback(newData, Actions.CREATE)
          }
        } else {
          callback(newData, Actions.UPDATE)
        }
      }

      onSuccess(t(`school_period.${config.action.toLowerCase()}.success`))
    }).catch(reason => {
      console.error(reason)

      if (mode === UpdateModes.OPTIMISTIC) {
        config.action === Actions.CREATE ? callback(schoolPeriod, Actions.DELETE) : callback(previousState, Actions.UPDATE)
      }

      onError(t(`school_period.${config.action.toLowerCase()}.error`))
    })
  }, [t, user, mode, callback])

  const del = useCallback(period => {
    return deleteSchoolPeriod(user, period).then(({ data }) => {
      if (isFunction(callback)) {
        callback(data, Actions.DELETE)
      }

      onSuccess(t('school_period.delete.success'))
    }).catch(error => {
      console.error(error)
      onError(t('school_period.delete.error'))
    })
  }, [t, user, mode, callback])

  const duplicate = useCallback((schoolPeriod, numberOfYears = 1) => upsert(new SchoolPeriod(schoolPeriod).clone({
    id: -1,
    startDate: initEuropeanDate(schoolPeriod.startDate).clone().add(numberOfYears, PeriodTypes.YEAR),
    endDate: initEuropeanDate(schoolPeriod.endDate).clone().add(numberOfYears, PeriodTypes.YEAR)
  })), [upsert])

  return { upsert, delete: del, duplicate }
}

export default useSchoolPeriod
